import { isRateLimited } from '../utils/rateLimit';
import { useRateLimitRetry } from '../utils/useRateLimitRetry';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { roleLandingPath } from '../utils/appRoles';

// Landing page for the Birey OAuth return. The Digipo backend redirects here with
// the freshly-minted Digipo JWT in the URL fragment (#token=…) on success, or
// #error=… on failure. We stash the token, load the profile, and route by role.
const AuthCallback: React.FC = () => {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const pendingToken = useRef<string | null>(null);
  const retry = useRateLimitRetry();
  const completeLogin = async (token: string) => {
    retry.clear();
    try {
      const role = await loginWithToken(token);
      navigate(role ? roleLandingPath(role) : '/login?birey_error=auth_failed', { replace: true });
    } catch (error) {
      if (isRateLimited(error)) retry.limited(error);
      else navigate('/login?birey_error=auth_failed', { replace: true });
    }
  };

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard
    ran.current = true;

    const frag = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = frag.get('token');
    const error = frag.get('error');
    const ad = frag.get('ad');
    // Scrub the token out of the URL immediately (history / bookmarks / referrer).
    window.history.replaceState(null, '', window.location.pathname);

    (async () => {
      if (token) {
        pendingToken.current = token;
        await completeLogin(token);
      } else if (error === 'no_entitlement') {
        // Authenticated, but the kurum has no Digipo → dedicated no-access screen.
        navigate(`/erisim-yok${ad ? `?ad=${encodeURIComponent(ad)}` : ''}`, { replace: true });
      } else {
        navigate(`/login${error ? `?birey_error=${encodeURIComponent(error)}` : ''}`, { replace: true });
      }
    })();
    // One fragment consumption; explicit retries use the retained token, never a new OAuth exchange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginWithToken, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fafafa] gap-4">
      {retry.message ? <>
        <p role="alert" className="text-[#20302a] text-center px-6">{retry.message}</p>
        <button type="button" disabled={retry.waiting} className="rounded-xl bg-[#20302a] px-6 py-3 text-white disabled:opacity-50"
          onClick={() => { if (pendingToken.current) void completeLogin(pendingToken.current); }}>
          Girişi tamamlamayı tekrar dene
        </button>
      </> : <>
        <div className="w-10 h-10 border-[3px] border-[#7bb51d] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#20302a] text-[15px] font-semibold">Birey ile giriş yapılıyor…</p>
      </>}
    </div>
  );
};

export default AuthCallback;
